/** 透明 PNG 导出：把同一份 SVG 场景栅格化，保证与预览一致。 */
import type { Project } from '../domain/types';
import { projectToSvg, unfoldedSizeMm } from './svg';

export interface PngOptions {
  /** 每毫米像素数；300 dpi ≈ 11.81 px/mm。 */
  pxPerMm?: number;
  /** 背景色，默认透明。 */
  background?: string | null;
}

export const DPI_PRESETS = [
  { label: '150 dpi（预览）', dpi: 150 },
  { label: '300 dpi（推荐打印）', dpi: 300 },
  { label: '600 dpi（高精度）', dpi: 600 },
] as const;

export const dpiToPxPerMm = (dpi: number): number => dpi / 25.4;

export async function projectToPngBlob(project: Project, options: PngOptions = {}): Promise<Blob> {
  const pxPerMm = options.pxPerMm ?? dpiToPxPerMm(300);
  const { widthMm, heightMm } = unfoldedSizeMm(project);
  const svg = projectToSvg(project, { background: options.background === null ? false : true });

  const width = Math.max(16, Math.round(widthMm * pxPerMm));
  const height = Math.max(16, Math.round(heightMm * pxPerMm));

  const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  try {
    const img = await loadImage(url);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('浏览器不支持 Canvas，无法导出 PNG。');
    if (options.background) {
      ctx.fillStyle = options.background;
      ctx.fillRect(0, 0, width, height);
    }
    ctx.drawImage(img, 0, 0, width, height);
    const out = await new Promise<Blob | null>((resolve) => canvas.toBlob((b) => resolve(b), 'image/png'));
    if (!out) throw new Error('PNG 编码失败，请尝试降低分辨率。');
    return out;
  } finally {
    URL.revokeObjectURL(url);
  }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('SVG 渲染失败，无法生成 PNG。'));
    img.src = src;
  });
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

export function downloadText(text: string, filename: string, type = 'text/plain;charset=utf-8'): void {
  downloadBlob(new Blob([text], { type }), filename);
}